import React from 'react';
import { OpenLR, ReferralTile } from '~/components/Patient';
import { SpinnerOverlay } from '~/components/SpinnerOverlay';
import { ServicesContext } from '~/services';
import { ReferralType } from '~/util';

export type OutgoingReferralType = { id: number; message: ReferralType };
interface OutgoingReferralsProps {
    patient: PatientType;
}
export function OutgoingReferrals({ patient }: OutgoingReferralsProps) {
    const services = React.useContext(ServicesContext);
    const [loading, setLoading] = React.useState(false);
    const [hasError, setHasError] = React.useState(0);
    const [outgoingReferrals, setOutgoingReferrals] = React.useState<OutgoingReferralType[]>([]);

    const loadReferrals = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await services.api.call({
                method: 'get',
                url: `/api/patients/${patient.id}/referrals`,
            });
            setOutgoingReferrals(
                result.data.sort(
                    (a: OutgoingReferralType, b: OutgoingReferralType) =>
                        new Date(b.message.updated).getTime() -
                        new Date(a.message.updated).getTime()
                )
            );
        } catch (e) {
            if (
                e.response.status === 400 &&
                e.response.data.message === 'User account is disabled.'
            ) {
                setHasError(2);
            } else {
                setHasError(1);
            }
        } finally {
            setLoading(false);
        }
    }, [services.api, patient.id]);

    React.useEffect(() => {
        loadReferrals();
    }, [loadReferrals]);

    return (
        <div className={loading ? 'mb-5' : ''}>
            <div className="mb-3 d-flex align-items-center justify-content-end">
                {services.user.hasFeatureToggle('outgoing_ref_feedback') && (
                    <a
                        href="https://survey.zohopublic.com/zs/kyClzv"
                        target="_blank"
                        rel="noreferrer"
                        className="me-3"
                    >
                        <i className="fa-solid fa-bullhorn me-1" />
                        Give Feedback
                    </a>
                )}
                <OpenLR isOutgoing />
            </div>
            <div className="position-relative">
                <SpinnerOverlay show={loading} />
                <div className="patient-gaps-container">
                    <div className="row">
                        {hasError === 0 && !loading && outgoingReferrals.length === 0 && (
                            <div className="text-center mt-5 mb-5">
                                There is no Referrals data available for this patient.
                            </div>
                        )}
                        {!loading && hasError === 1 && (
                            <div className="text-center mt-5 mb-5">
                                Apologies, the requested data could not be retrieved at this time.
                                Please try again later.
                                <br />
                                If the issue persists, please contact the LeadingReach support team
                                at{' '}
                                <a href="mailto:HelpDesk@leadingreach.com">
                                    HelpDesk@leadingreach.com
                                </a>{' '}
                                or 1-866-656-4410.
                            </div>
                        )}
                        {!loading && hasError === 2 && (
                            <div className="text-center mt-5 mb-5">
                                You cannot complete this action because your LeadingReach user
                                linked to Zephyr is disabled. Please link to an active LeadingReach
                                user or contact your LeadingReach administrator.
                            </div>
                        )}
                        {hasError === 0 &&
                            outgoingReferrals.map((outgoingReferral) => (
                                <ReferralTile
                                    key={outgoingReferral.id}
                                    outgoingReferral={outgoingReferral}
                                />
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
